import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { Formik } from "formik";
import {
  ArableField,
  FERTILIZER_APPLICATION_RATE_UNIT_OPTIONS,
  FERTILIZER_TOTAL_AMOUNT_UNIT,
  FERTILIZER_TYPES,
  FertilizerEvent,
  Field,
  NITROGEN_FERTILIZER_TYPE,
  ORGANIC_FERTILIZER_ANIMAL_TYPES,
  ORGANIC_FERTILIZER_APPLICATION_METHOD,
  ORGANIC_FERTILIZER_TYPES,
  useArableFields,
  useFields,
} from "model";
import { useCurrentFarm } from "model/useFarmQueries";
import { MutableRefObject, ReactNode } from "react";

import EnterDate from "features/general/EnterDate";
import * as Yup from "yup";
import EnterFertilizerEventFertilizerProperties from "./EnterFertilizerEventProperties";
import {
  AnyOtherFertilizerEvent,
  FertilizerEventFormData,
  LimeApplicationEvent,
  OrganicFertilizerApplicationEvent,
  SyntheticFertilizerApplicationEvent,
} from "./FertilizerEventFormData";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  p: 2.5,
};

interface Props {
  fertilizerEvent: FertilizerEvent | undefined;
  handleSave: (
    fertilizerInput: FertilizerEventFormData,
    farmGuid: string
  ) => void;
  fieldSelectDisabled: boolean;
  dirtyRef: MutableRefObject<boolean>;
  fileUploadSlot?: ReactNode;
  children: (args: {
    steps: { content: ReactNode }[];
    submitForm: () => Promise<void>;
  }) => ReactNode;
}

const convertToFormInput = (
  fertilizerEvent: FertilizerEvent
): FertilizerEventFormData => {
  return {
    note: fertilizerEvent.note ?? undefined,
    fertilizerApplicationDate: new Date(
      fertilizerEvent.fertilizerApplicationDate
    ),
    fertilizerType: fertilizerEvent.fertilizerProperties.fertilizerType,
    fieldGuids: fertilizerEvent.fieldGuids ?? [],
    fertilizerProperties: {
      ...getDefaultFertilizerProperties(),
      ...fertilizerEvent.fertilizerProperties,
    } as
      | OrganicFertilizerApplicationEvent
      | SyntheticFertilizerApplicationEvent
      | LimeApplicationEvent
      | AnyOtherFertilizerEvent,
    totalAmount: fertilizerEvent.totalAmount,
    totalAmountUnit: fertilizerEvent.totalAmountUnit,
  };
};

const getDefaultFertilizerProperties = ():
  | OrganicFertilizerApplicationEvent
  | SyntheticFertilizerApplicationEvent
  | LimeApplicationEvent
  | AnyOtherFertilizerEvent => {
  return {
    isImported: false,
    organicFertilizerType: "" as unknown as ORGANIC_FERTILIZER_TYPES,
    animalType: [],
    applicationMethod: "" as unknown as ORGANIC_FERTILIZER_APPLICATION_METHOD,
    productName: "",
    nPercentage: "" as unknown as number,
    typeOfNitrogen: "" as unknown as NITROGEN_FERTILIZER_TYPE,
    pPercentage: "" as unknown as number,
    kPercentage: "" as unknown as number,
    phPriorToApplication: "" as unknown as number,
  };
};

export default function EnterFertilizerEvent({
  fertilizerEvent,
  handleSave,
  fieldSelectDisabled,
  dirtyRef,
  fileUploadSlot,
  children,
}: Props) {
  const { t } = useTranslation();

  const { data: currentFarm } = useCurrentFarm();
  const { data: allGrassFields = [], isLoading: fieldsLoading } = useFields();
  const { data: allArableFields = [], isLoading: arableLoading } =
    useArableFields();
  const loading = fieldsLoading || arableLoading;

  let allFields: (Field | ArableField)[] = [];
  allFields = allFields.concat(allArableFields);
  allFields = allFields.concat(allGrassFields);

  // OrganicFertilizerApplicationEvent Yup Schema
  const OrganicFertilizerApplicationEventSchema = Yup.object({
    isImported: Yup.boolean().required("isImported is required"),
    organicFertilizerType: Yup.mixed<ORGANIC_FERTILIZER_TYPES>()
      .oneOf(Object.values(ORGANIC_FERTILIZER_TYPES))
      .required("Organic fertilizer type is required"),
    animalType: Yup.array()
      .of(
        Yup.mixed<ORGANIC_FERTILIZER_ANIMAL_TYPES>().oneOf(
          Object.values(ORGANIC_FERTILIZER_ANIMAL_TYPES)
        )
      )
      .min(1)
      .required("Animal type is required"),
    applicationMethod: Yup.mixed<ORGANIC_FERTILIZER_APPLICATION_METHOD>()
      .oneOf(Object.values(ORGANIC_FERTILIZER_APPLICATION_METHOD))
      .required("Application method is required"),
  });

  // SyntheticFertilizerApplicationEvent Yup Schema
  const SyntheticFertilizerApplicationEventSchema = Yup.object({
    productName: Yup.string().required("Product name is required"),
    nPercentage: Yup.number()
      .min(0, "N percentage must be at least 0")
      .max(100, "N percentage must be at most 100")
      .required("N percentage is required"),
    typeOfNitrogen: Yup.mixed<NITROGEN_FERTILIZER_TYPE>()
      .oneOf(Object.values(NITROGEN_FERTILIZER_TYPE))
      .required("Nitrogen type is required"),
    pPercentage: Yup.number().min(0).max(100).optional(),
    kPercentage: Yup.number().min(0).max(100).optional(),
  });

  // LimeApplicationEvent Yup Schema
  const LimeApplicationEventSchema = Yup.object({
    phPriorToApplication: Yup.number()
      .min(0)
      .max(14)
      .required("PH prior to application is required"),
  });

  // AnyOtherFertilizerEvent Yup Schema
  const AnyOtherFertilizerEventSchema = Yup.object({});

  const validNumber = (min = 0) =>
    Yup.number()
      .typeError(t("edit.minNumber.error", { min }))
      .moreThan(min, t("edit.minNumber.error", { min }))
      .required(t("edit.required.error"));

  // FertilizerEventFormData Yup Schema
  const FertilizerEventFormDataSchema = Yup.object({
    note: Yup.string().optional(),
    totalAmount: validNumber(0).required("Application rate is required"),
    totalAmountUnit: Yup.mixed<FERTILIZER_TOTAL_AMOUNT_UNIT>()
      .oneOf(Object.values(FERTILIZER_TOTAL_AMOUNT_UNIT))
      .required("Unit is required"),
    fertilizerApplicationDate: Yup.date().required(
      "Fertilizer application date is required"
    ),
    fertilizerType: Yup.mixed<FERTILIZER_TYPES>()
      .oneOf(Object.values(FERTILIZER_TYPES))
      .required("Fertilizer type is required"),
    fieldGuids: Yup.array()
      .of(Yup.string().required())
      .min(1)
      .required("At least one field guid is required"),

    // Conditional validation for fertilizerProperties based on fertilizerType
    fertilizerProperties: Yup.mixed().when(
      "fertilizerType",
      (fertilizerType: FERTILIZER_TYPES[]) => {
        if (fertilizerType[0] === FERTILIZER_TYPES.ORGANIC) {
          return OrganicFertilizerApplicationEventSchema;
        } else if (fertilizerType[0] === FERTILIZER_TYPES.SYNTHETIC) {
          return SyntheticFertilizerApplicationEventSchema;
        } else if (fertilizerType[0] === FERTILIZER_TYPES.LIME) {
          return LimeApplicationEventSchema;
        } else {
          return AnyOtherFertilizerEventSchema;
        }
      }
    ),
  });

  if (loading) {
    return null;
  }

  const initialFertilizerEvent = fertilizerEvent
    ? convertToFormInput(fertilizerEvent)
    : {
        totalAmount: "" as unknown as number,
        totalAmountUnit: "" as FERTILIZER_TOTAL_AMOUNT_UNIT,
        fertilizerApplicationDate: new Date(),
        fertilizerType: "" as unknown as FERTILIZER_TYPES,
        fieldGuids: [],
        fertilizerProperties: getDefaultFertilizerProperties(),
      };
  return (
    <Formik
      enableReinitialize={false}
      initialValues={initialFertilizerEvent}
      validationSchema={FertilizerEventFormDataSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(fertilizerEventFormData: FertilizerEventFormData) => {
        void handleSave(fertilizerEventFormData, currentFarm?.guid ?? "");
      }}
    >
      {({
        values,
        handleChange,
        errors,
        validateField,
        setFieldValue,
        dirty,
        submitForm,
      }) => {
        dirtyRef.current = dirty;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleInputChange = async (e: any, fieldName: string) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (handleChange(e) as any);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (validateField(fieldName) as any);
        };

        const handleFieldToggle = async (fieldGuid: string) => {
          if (fieldSelectDisabled) return;
          const isSelected = values.fieldGuids.includes(fieldGuid);
          const updated = isSelected
            ? values.fieldGuids.filter((g: string) => g !== fieldGuid)
            : [...values.fieldGuids, fieldGuid];
          await setFieldValue("fieldGuids", updated);
        };

        const steps = [
          {
            content: (
              <Container style={{ textAlign: "left" }}>
                <Grid container spacing={2.5}>
                  {/* Date & Fertilizer Type */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("fertilizer.date.type.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <EnterDate
                            disabled={false}
                            date={values.fertilizerApplicationDate}
                            setDate={async (date: Date | null) => {
                              await setFieldValue(
                                "fertilizerApplicationDate",
                                date,
                                false
                              );
                            }}
                            dateError={!!errors.fertilizerApplicationDate}
                            name={"fertilizerApplicationDate"}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            select
                            fullWidth
                            required
                            value={values.fertilizerType ?? ""}
                            onChange={async (event) => {
                              await handleInputChange(event, `fertilizerType`);
                            }}
                            error={!!errors.fertilizerType}
                            label={t("fertilizerInput.fertilizerType.label")}
                            name={`fertilizerType`}
                          >
                            {Object.entries(FERTILIZER_TYPES)
                              .sort(([, a], [, b]) => a.localeCompare(b))
                              .map(([key, value]) => (
                                <MenuItem key={key} value={value}>
                                  {t(
                                    `fertilizerEvent.fertilizerType.label.${value}`
                                  )}
                                </MenuItem>
                              ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Fertilizer Properties — only for types that have them */}
                  {(values.fertilizerType === FERTILIZER_TYPES.ORGANIC ||
                    values.fertilizerType === FERTILIZER_TYPES.SYNTHETIC ||
                    values.fertilizerType === FERTILIZER_TYPES.LIME) && (
                    <Grid size={12}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {t("fertilizer.properties.heading")}
                      </Typography>
                      <Box sx={cardSx}>
                        <EnterFertilizerEventFertilizerProperties
                          fertilizerProperties={values.fertilizerProperties}
                          handleInputChange={handleInputChange}
                          setFieldValue={setFieldValue}
                          fertilizerType={values.fertilizerType}
                          errors={errors}
                        />
                      </Box>
                    </Grid>
                  )}

                  {/* Field selection checkbox list */}
                  <Grid size={12}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontWeight: 500,
                        color: errors.fieldGuids ? "error.main" : undefined,
                      }}
                    >
                      {t("fertilizerEventInput.fields.text")}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: errors.fieldGuids
                          ? "error.main"
                          : "rgba(0, 0, 0, 0.12)",
                        backgroundColor: "white",
                        maxHeight: 220,
                        overflowY: "auto",
                        py: 0.5,
                        px: 1,
                        opacity: fieldSelectDisabled ? 0.5 : 1,
                      }}
                    >
                      {[...allFields]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((field) => {
                          const isSelected = values.fieldGuids.includes(
                            field.guid
                          );
                          return (
                            <FormControlLabel
                              key={field.guid}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleFieldToggle(field.guid)}
                                  size="small"
                                  sx={{
                                    color: "rgba(0,0,0,0.3)",
                                    "&.Mui-checked": { color: "#7EC254" },
                                  }}
                                />
                              }
                              label={field.name}
                              sx={{
                                mx: 0,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.875rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          );
                        })}
                    </Box>
                  </Grid>
                  {/* Amount */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("fertilizer.amount.cost.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            required
                            value={values.totalAmount}
                            type="number"
                            id="totalAmount"
                            error={!!errors.totalAmount}
                            onChange={async (event) => {
                              await handleInputChange(event, `totalAmount`);
                            }}
                            label={t("fertilizerEvent.totalAmount.label")}
                            name={`totalAmount`}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            select
                            fullWidth
                            required
                            value={values.totalAmountUnit ?? ""}
                            onChange={async (event) => {
                              await handleInputChange(event, `totalAmountUnit`);
                            }}
                            error={!!errors.totalAmountUnit}
                            label={t("fertilizerInput.totalAmountUnit.label")}
                            name={`totalAmountUnit`}
                          >
                            {FERTILIZER_APPLICATION_RATE_UNIT_OPTIONS.map(
                              (value) => (
                                <MenuItem key={value} value={value}>
                                  {t(
                                    `fertilizerEvent.totalAmountUnit.option.${value}`
                                  )}
                                </MenuItem>
                              )
                            )}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Additional details (optional) */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("common.additionalDetails.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <TextField
                            id="fertilizer-event-input-note"
                            label={t("reseedEvent.notes.label")}
                            value={values.note}
                            helperText={errors.note}
                            error={!!errors.note}
                            fullWidth
                            onChange={(e) => handleInputChange(e, "note")}
                            name={"note"}
                            multiline
                            maxRows={12}
                          />
                        </Grid>
                        {fileUploadSlot && (
                          <Grid size={12}>{fileUploadSlot}</Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            ),
          },
        ];

        return <>{children({ steps, submitForm })}</>;
      }}
    </Formik>
  );
}
